import { type OperationVariables } from '@apollo/react-common';
import { type DataProxy } from 'apollo-cache';
import { type GraphQLTaggedNode } from 'react-relay';
import {
	createOperationDescriptor,
	type ConcreteRequest,
	getRequest,
	type RecordSourceProxy,
	type ReaderSelection,
} from 'relay-runtime';

import { isRelayEnabled } from './isRelayEnabled.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import {
	syncQueryCacheBridge,
	type QueryCacheBridgeSource,
} from './cacheBridge/queryCacheBridgeSync.ts';
import { getAmbientStoreProxy } from './optimisticInterop/ambientStoreProxy.ts';
import { writeQueryFields } from './relayQueryWriteOperations.ts';
import { writeQueryPayloadToStore } from './relayStorePayloadWrite.ts';
import { getRelayOperationName, getRelayOperationVariablesWithDefaults } from './relayOperation.ts';

export type WriteQueryInteropArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<DataProxy.WriteQueryOptions<TData, TVariables>, 'data' | 'variables'> & {
	query: GraphQLTaggedNode;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export function writeQueryInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: WriteQueryInteropArgs<TData, TVariables>): void {
	const operationName = getRelayOperationName(params.query);
	const source: QueryCacheBridgeSource = isRelayEnabled(params.__callerPackage__, {
		operationName,
		phase: 'cache',
	})
		? 'relay'
		: 'apollo';

	// Relay builds field storage keys from the operation's *resolved* variables, so a query declaring
	// `$first: Int = 20` normalizes under `field(...,first:20)` even when the caller omits `first`.
	// Resolving the artifact's declared defaults here keeps every Relay write below on the same
	// records Relay normalized and the UI subscribes to; using the raw variables would silently
	// write to a sibling record nobody reads. See getRelayOperationVariablesWithDefaults.
	const variables = getRelayOperationVariablesWithDefaults(params.query, params.variables);

	// Inside a Relay-native optimistic layer (commitMutation optimisticUpdater/updater), route the
	// write onto the provided store proxy so it participates in Relay's disposable layer instead of
	// committing permanently. This upserts records (incl. connection list changes) via the store
	// proxy — the capability the in-place path lacks — so no snapshot/replay rollback is needed;
	// Relay reverts the whole layer atomically. The cache bridge is intentionally skipped here.
	const ambientStore = getAmbientStoreProxy();
	if (ambientStore && source === 'relay' && params.data) {
		const queryNode = params.query as ConcreteRequest;
		const selections = queryNode?.operation?.selections;
		if (selections) {
			writeQueryPayloadToStore(
				ambientStore,
				// Normalization selections are a superset of the reader selections the primitive walks
				// (scalar/linked/inline-fragment/condition); matches the cast in writeQueryInteropRelay.
				selections as readonly ReaderSelection[],
				params.data as Record<string, unknown>,
				variables as Record<string, unknown>,
			);
			return;
		}
	}

	const didWrite =
		source === 'relay'
			? writeQueryInteropRelay<TData, TVariables>(params, variables)
			: writeQueryInteropApollo<TData, TVariables>(params);

	if (didWrite) {
		syncQueryCacheBridge({
			// The bridge mirrors into the *other* cache, which applies its own document defaults, so it
			// keeps receiving the caller's variables rather than the Relay-resolved ones.
			source,
			query: params.query,
			variables: params.variables,
			data: params.data,
		});
	}
}

function writeQueryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: WriteQueryInteropArgs<TData, TVariables>,
	variables: Record<string, any>,
): boolean {
	const relayEnv = window.GLOBAL_RELAY_ENV;
	const { query, data } = params;

	if (!relayEnv) {
		throw new Error(
			`[Relay migration] Missing GLOBAL_RELAY_ENV for writeQueryInterop; ${params.__callerPackage__}.`,
		);
	}

	if (!data) {
		return false;
	}

	let didWrite = false;
	relayEnv.commitUpdate((storeProxy: RecordSourceProxy) => {
		const root = storeProxy.getRoot();
		if (!root) {
			return;
		}

		const queryNode = query as ConcreteRequest;
		const queryFields = queryNode?.operation?.selections;

		if (queryFields) {
			didWrite = writeQueryFields(root, queryFields as readonly ReaderSelection[], data, variables);
		}
	});

	if (!didWrite) {
		// If no existing Relay records were updated, fall back to Relay's normal payload
		// write path. Invalid Relay artifacts should still throw here, preserving the
		// original writeQueryInterop failure behavior.
		const operation = createOperationDescriptor(getRequest(query), variables);
		relayEnv.commitPayload(operation, data as Record<string, unknown>);
		didWrite = true;
	}

	return didWrite;
}

function writeQueryInteropApollo<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: WriteQueryInteropArgs<TData, TVariables>): boolean {
	window['GLOBAL_APOLLO_CLIENT']!.writeQuery({
		...params,
		query: toDocumentNode(params.query),
	});
	return true;
}
